import { LocalStorage } from 'src/app/class/local-storage';
import { Injectable } from '@angular/core';
import { IUser } from 'src/app/interface/user';
import { CONSTANTS } from 'src/app/config/constants';
import { ICompany } from 'src/app/interface/company';
import { HttpClient } from '@angular/common/http';
import { Observable, Subject } from 'rxjs';
import { environment } from 'src/environments/environment';
import { tap } from 'rxjs/operators';
import { Preferences } from '@capacitor/preferences';

@Injectable({
	providedIn: 'root',
})
export class AuthService {
	private signedInSubject: Subject<ILoginResponse>;
	private signedOutSubject: Subject<any>;

	constructor(private httpClient: HttpClient) {
		this.signedInSubject = new Subject<ILoginResponse>();
		this.signedOutSubject = new Subject<any>();
	}

	public set user(data: IUser) {
		let company: ICompany | undefined;
		if (data.company) {
			company = { ...data.company };
			delete data.company;
		} else if (data.user_company?.company) {
			company = { ...data.user_company?.company };
			delete data.user_company.company;
		}
		LocalStorage.set(CONSTANTS.localStorage.keys.user, data);
		if (!company) {
			return;
		}
		this.company = company;
	}

	public get user(): IUser {
		return LocalStorage.get(CONSTANTS.localStorage.keys.user) as IUser;
	}

	public set company(data: ICompany) {
		LocalStorage.set(CONSTANTS.localStorage.keys.company, data);
	}

	public get company(): ICompany {
		return LocalStorage.get(CONSTANTS.localStorage.keys.company)
	}

	public set token(value: string) {
		if (!value?.length) {
			LocalStorage.remove(CONSTANTS.localStorage.keys.authToken);
			Preferences.remove({ key: CONSTANTS.localStorage.keys.authToken });
			return;
		}
		LocalStorage.set(CONSTANTS.localStorage.keys.authToken, value);
		Preferences.set({ key: CONSTANTS.localStorage.keys.authToken, value });
	}

	public get token(): string {
		return LocalStorage.get(CONSTANTS.localStorage.keys.authToken);
	}

	public setShiftFromCrew(crew: Array<IUser>) {
		const user = this.user;
		const amIInvolved = crew?.find((member) => {
			if (member?.id && member?.id != user?.id) {
				return false;
			}
			if (user?.shift && member?.shift?.id < user?.shift?.id) {
				return false;
			}
			return true;
		});
		if (!user.shift || amIInvolved) {
			user.shift = amIInvolved?.shift;
			this.user = user;
		}
	}

	public login(data: ILogin): Observable<ILoginResponse> {
		return this.httpClient
			.post<ILoginResponse>(environment.apiUrl + '/login', data)
			.pipe(
				tap((response) => {
					if (response.user) {
						this.user = response.user;
					}
					if (response.token) {
						this.token = response.token;
					}
					if (response.user && response.token) {
						this.signedInSubject.next({} as ILoginResponse);
					}
					return response;
				})
			);
	}

	public facebook(data: any = null): Observable<any> {
		return this.httpClient
			.post<any>(environment.apiUrl + '/facebook/login', data)
			.pipe(
				tap((response) => {
					if (response.user) {
						this.user = response.user;
					}
					if (response.token) {
						this.token = response.token;
					}
					if (response.user && response.token) {
						this.signedInSubject.next(response);
					}
					return response;
				})
			);
	}

	public google(data: any = null): Observable<any> {
		return this.httpClient
			.post<any>(environment.apiUrl + '/google/login', data)
			.pipe(
				tap((response) => {
					if (response.user) {
						this.user = response.user;
					}
					if (response.token) {
						this.token = response.token;
					}
					if (response.user && response.token) {
						this.signedInSubject.next(response);
					}
					return response;
				})
			);
	}

	public linkedIn(data: any = null): Observable<any> {
		return this.httpClient
			.post<any>(environment.apiUrl + '/linkedin/login', data)
			.pipe(
				tap((response) => {
					if (response.user) {
						this.user = response.user;
					}
					if (response.token) {
						this.token = response.token;
					}
					if (response.user && response.token) {
						this.signedInSubject.next(response);
					}
					return response;
				})
			);
	}

	public microsoft(data: any = null): Observable<any> {
		return this.httpClient
			.post<any>(environment.apiUrl + '/microsoft/login', data)
			.pipe(
				tap((response) => {
					if (response.user) {
						this.user = response.user;
					}
					if (response.token) {
						this.token = response.token;
					}
					if (response.user && response.token) {
						this.signedInSubject.next(response);
					}
					return response;
				})
			);
	}

	public apple(data: any = null): Observable<any> {
		return this.httpClient
			.post<any>(environment.apiUrl + '/apple/login', data)
			.pipe(
				tap((response) => {
					if (response.user) {
						this.user = response.user;
					}
					if (response.token) {
						this.token = response.token;
					}
					if (response.user && response.token) {
						this.signedInSubject.next(response);
					}
					return response;
				})
			);
	}

	public signedIn() {
		return this.signedInSubject.asObservable();
	}

	public fireSignedIn() {
		this.signedInSubject.next({} as ILoginResponse);
	}

	public signedOut() {
		return this.signedOutSubject.asObservable();
	}

	public logout() {
		this.signedOutSubject.next(null);
	}
}

interface ILogin {
	email_phone: string;
	password: string;
}

interface ILoginResponse {
	user: IUser;
	token: string;
}
